/**
 * Zerbay photo shimmer controller (BASE-63, HDR-27, CART-40, COL-39, HOME-43, PDPM-13).
 *
 * CONTRACT (parts build against this; keep it in sync with the BASE-63 block of assets/zerbay-base.css)
 *
 *  - A photo BOX is an element with class `zb-shimmer`, or an element matching one of the Horizon-owned `selectors` below.
 *  - The MANAGED IMAGE of a box is its first <img> that carries neither `data-zb-shimmer-skip` nor `hidden`. Parts mark every
 *    extra image (hover, secondary photos) with `data-zb-shimmer-skip`: only the managed image is waited for, but the CSS hides
 *    EVERY <img> without `data-zb-shimmer-skip` in a loading box (filter: opacity, so a part's own opacity fades are untouched).
 *  - While the managed image has not finished loading the box carries `is-loading`. It settles on the image's load or error
 *    event (a finished request that failed, or had no source, counts as settled: nothing more will arrive). On settle the box
 *    gets a short-lived `is-settling` (CSS fade-in). A timer is the only way `is-settling` is removed again (a later
 *    `is-loading` or a new settle clears it first); no animationend listener is used, so reduced motion needs no special case.
 *  - `is-near` is toggled by an IntersectionObserver (200px margin) so only boxes near the viewport animate. At 990px and up
 *    Horizon scrolls inside `.page-wrapper`, not the document: the observer's root is the viewport, so `rootMargin` acts on the
 *    viewport only and a box far down inside that container is "near" only once it is within 200px of the viewport (HZD-57).
 *  - `data-zb-shimmer-keep` on a box (opt-in, e.g. cards with a colour preview): once the box has shown a photo, a later
 *    `src` / `srcset` swap does NOT re-mark it; the old photo stays visible until the new one has loaded.
 *  - `data-zb-shimmer-bg="<url>"` on a box: the photo is a CSS background. The URL is probed once per URL (a changed or removed
 *    attribute is observed and invalidates the earlier probe). The URL must be byte-equal to the URL in the CSS, or the probe
 *    warms a different cache entry. While the attribute is set the probe owns the box: image load / error events inside the
 *    box never settle it (the box is re-read), and the probe never reads the box's images; the two requests do not settle
 *    each other's box.
 *  - Morph safe: boxes added later are found by a MutationObserver; a morph that strips `is-loading` / `is-near` is repaired
 *    (the box is re-marked and re-observed: a class change that disagrees with the state the controller last wrote asks the
 *    IntersectionObserver for a fresh entry, so the controller's own class writes never trigger it); removing the managed
 *    image from a loading box settles the box.
 *  - A load / error event counts only for the image that is still the box's managed image; an image that was swapped out,
 *    marked `hidden` or marked `data-zb-shimmer-skip` in the meantime is ignored and the box is re-read. Changes to the
 *    `hidden` and `data-zb-shimmer-skip` attributes of an image re-sync its box.
 *  - Parts render `zb-shimmer` server-side (in the Liquid markup): a `zb-shimmer` class added to an element at runtime is NOT
 *    picked up until `scan(root)` is called for it.
 *
 * Public API: window.Zerbay.shimmer = { scan, selectors }. `scan(root)` re-evaluates every box in `root`.
 * Keep this file loaded as <script type="module">; a second evaluation (same page, other URL) does nothing.
 */

/** Horizon-owned photo boxes (parts tag their own boxes with .zb-shimmer). Extend through a base topic only, and add the
 *  same selector to the BASE-63 rules in assets/zerbay-base.css. Frozen: callers cannot change it at runtime. */
export const selectors = Object.freeze(['.cart-items__media-container']);

const NEAR_MARGIN = '200px';
const SETTLE_MS = 400; // longer than the 0.18s CSS fade; the timer is the only removal path of is-settling
const MANAGED_IMG = 'img:not([data-zb-shimmer-skip]):not([hidden])';

/** @type {Set<Element>} boxes the proximity observer holds (loading boxes only) */
const watched = new Set();
/** @type {Set<Element>} boxes this script marked `is-loading` and has not settled */
const loading = new Set();
/** @type {Set<Element>} boxes whose `is-near` class the controller last wrote as present (compared with class mutations) */
const nearState = new Set();
/** @type {WeakSet<Element>} boxes that have shown a photo (for data-zb-shimmer-keep) */
const shown = new WeakSet();
/** @type {WeakMap<Element, {url: string}>} the current background probe per box (identity = the probe request) */
const probes = new WeakMap();
/** @type {WeakSet<Element>} images that already have a pending load/error listener */
const armed = new WeakSet();
/** @type {WeakMap<Element, number>} pending is-settling removal timers */
const settleTimers = new WeakMap();

const near =
  typeof IntersectionObserver === 'function'
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            // an entry queued before the box was unwatched (settled, detached) must not bring `is-near` back
            if (!watched.has(entry.target)) continue;
            // record the intended state before writing the class, so the class mutation that follows reads as our own
            if (entry.isIntersecting) nearState.add(entry.target);
            else nearState.delete(entry.target);
            entry.target.classList.toggle('is-near', entry.isIntersecting);
          }
        },
        { rootMargin: NEAR_MARGIN }
      )
    : null;

const boxSelector = () => ['.zb-shimmer', ...selectors].join(', ');

function watch(box) {
  if (!near) {
    watched.add(box);
    nearState.add(box);
    box.classList.add('is-near');
    return;
  }
  // Re-observing hands the observer a fresh entry, so a box whose `is-near` was stripped gets it back.
  if (watched.has(box)) near.unobserve(box);
  watched.add(box);
  near.observe(box);
}

function unwatch(box) {
  nearState.delete(box);
  box.classList.remove('is-near');
  if (watched.delete(box) && near) near.unobserve(box);
}

/** Drop everything held for boxes that left the document. */
function releaseDetached() {
  for (const box of [...watched, ...loading]) {
    if (!box.isConnected) {
      unwatch(box);
      loading.delete(box);
    }
  }
}

function markLoading(box) {
  const fresh = !box.classList.contains('is-loading');
  loading.add(box);
  if (fresh) {
    clearSettling(box);
    box.classList.add('is-loading');
  }
  if (fresh || !watched.has(box)) watch(box);
}

function clearSettling(box) {
  const timer = settleTimers.get(box);
  if (timer !== undefined) {
    clearTimeout(timer);
    settleTimers.delete(box);
  }
  box.classList.remove('is-settling');
}

function settle(box) {
  const wasLoading = loading.delete(box) || box.classList.contains('is-loading');
  box.classList.remove('is-loading');
  unwatch(box);
  if (!wasLoading) return;
  clearSettling(box);
  box.classList.add('is-settling');
  settleTimers.set(
    box,
    setTimeout(() => {
      settleTimers.delete(box);
      box.classList.remove('is-settling');
    }, SETTLE_MS)
  );
}

function probeBackground(box, url) {
  const current = probes.get(box);
  if (current && current.url === url) {
    // Same URL: nothing to request again; only repair state a morph may have stripped while the probe is still out.
    if (loading.has(box)) markLoading(box);
    return;
  }
  const probe = { url };
  probes.set(box, probe);
  markLoading(box);
  const image = new Image();
  const done = (event) => {
    if (probes.get(box) !== probe) return;
    if (event.type === 'load') shown.add(box);
    settle(box);
  };
  image.addEventListener('load', done, { once: true });
  image.addEventListener('error', done, { once: true });
  image.src = url;
}

/** Look at one box and bring its classes in line with the state of its photo. */
function sync(box) {
  const bg = box.getAttribute('data-zb-shimmer-bg');
  if (bg) {
    probeBackground(box, bg);
    return;
  }
  if (probes.has(box)) probes.delete(box); // attribute removed: the old probe no longer counts

  const img = box.querySelector(MANAGED_IMG);
  if (!img) {
    // No managed photo (it was taken out of a loading box, or only skip / hidden images remain): nothing to wait for.
    if (loading.has(box) || box.classList.contains('is-loading')) settle(box);
    return;
  }

  if (img.complete) {
    if (img.naturalWidth > 0) shown.add(box);
    if (loading.has(box) || box.classList.contains('is-loading')) settle(box);
    return;
  }

  // keep: a box that already showed a photo keeps it on screen until the replacement has loaded
  const keep = box.hasAttribute('data-zb-shimmer-keep') && shown.has(box);
  if (!keep) markLoading(box);
  if (armed.has(img)) return;
  armed.add(img);
  const onDone = (event) => {
    armed.delete(img);
    img.removeEventListener('load', onDone);
    img.removeEventListener('error', onDone);
    // The photo may sit in another box by now (morph); look at the box that holds it.
    const holder = img.closest(boxSelector());
    if (!holder?.isConnected) return;
    // A background probe owns the box while data-zb-shimmer-bg is set: an image event never settles it, the box is re-read.
    if (holder.hasAttribute('data-zb-shimmer-bg')) {
      sync(holder);
      return;
    }
    // Only the box's current managed image settles it. An image that was hidden, marked skip or pushed behind another one
    // while it loaded is ignored: the box is re-read and keeps waiting for its real managed image.
    if (holder.querySelector(MANAGED_IMG) !== img) {
      sync(holder);
      return;
    }
    if (event.type === 'error') settle(holder);
    else sync(holder);
  };
  img.addEventListener('load', onDone);
  img.addEventListener('error', onDone);
}

/**
 * Mark every photo box inside `root` (and `root` itself, and the box that holds it).
 * @param {ParentNode | Element} [root]
 */
export function scan(root = document) {
  if (!root || (root.nodeType !== 1 && root.nodeType !== 9)) return;
  const selector = boxSelector();
  if (root.nodeType === 1 && root.matches(selector)) sync(root);
  for (const box of root.querySelectorAll(selector)) sync(box);
  if (root.nodeType === 1) {
    const holder = root.closest(selector);
    if (holder && holder !== root) sync(holder);
  }
}

function onMutations(records) {
  const selector = boxSelector();
  const resync = new Set();
  let removed = false;

  for (const record of records) {
    if (record.type === 'attributes') {
      const target = record.target;
      if (record.attributeName === 'class') {
        // A morph reset the class list of a box we are still waiting on: put the state back.
        if (loading.has(target)) {
          if (!target.classList.contains('is-loading')) resync.add(target);
          // a lost (or foreign) is-near: ask the proximity observer for a fresh entry. Our own writes agree with nearState.
          else if (nearState.has(target) !== target.classList.contains('is-near')) watch(target);
        }
        continue;
      }
      // src / srcset / hidden / data-zb-shimmer-skip of a photo, or data-zb-shimmer-bg of a box
      const box = target.closest?.(selector);
      if (box) resync.add(box);
      continue;
    }
    for (const node of record.addedNodes) {
      if (node.nodeType === 1) scan(node);
    }
    if (record.removedNodes.length) {
      removed = true;
      // The managed image may have left a box that is still in the document.
      const box = record.target.closest?.(selector);
      if (box) resync.add(box);
    }
  }

  if (resync.size) {
    // Let the browser start the new request / finish the morph before reading the image state.
    Promise.resolve().then(() => {
      for (const box of resync) if (box.isConnected) sync(box);
    });
  }
  if (removed) releaseDetached();
}

function start() {
  scan(document);
  // Horizon morphs sections and drawers (Section Rendering API): watch additions, removals, image swaps, class resets.
  new MutationObserver(onMutations).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', 'hidden', 'data-zb-shimmer-skip', 'data-zb-shimmer-bg', 'class'],
  });
}

if (!window.Zerbay?.shimmer) {
  window.Zerbay = window.Zerbay || {};
  window.Zerbay.shimmer = { scan, selectors };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}
